// Mercapoli - City Selector Modal
// Modal obligatorio para selección de ciudad
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronRight, Loader2, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { ciudades as ciudadesApi, config } from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

// Banderas por país
const BANDERAS = {
  'GT': '🇬🇹', 'MX': '🇲🇽', 'CO': '🇨🇴', 'PE': '🇵🇪', 'CL': '🇨🇱',
  'AR': '🇦🇷', 'US': '🇺🇸', 'CR': '🇨🇷', 'PA': '🇵🇦', 'SV': '🇸🇻',
  'HN': '🇭🇳', 'NI': '🇳🇮', 'EC': '🇪🇨'
};

const CitySelectorModal = ({ isOpen, onSelect, initialPais = null }) => {
  const [step, setStep] = useState(initialPais ? 'ciudad' : 'pais');
  const [selectedPais, setSelectedPais] = useState(initialPais);
  const [paises, setPaises] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPaises, setLoadingPaises] = useState(true);

  // Cargar países al montar
  useEffect(() => {
    loadPaises();
  }, []);

  // Cargar ciudades cuando se selecciona un país
  useEffect(() => {
    if (selectedPais) {
      loadCiudades(selectedPais);
    }
  }, [selectedPais]);

  const loadPaises = async () => {
    setLoadingPaises(true);
    try {
      const data = await config.getPaises();
      setPaises(data.paises || []);
    } catch (error) {
      console.error('Error loading countries:', error);
      // Fallback mínimo
      setPaises([
        { codigo: 'GT', nombre: 'Guatemala' },
        { codigo: 'MX', nombre: 'México' },
        { codigo: 'CO', nombre: 'Colombia' }
      ]);
    }
    setLoadingPaises(false);
  };

  const loadCiudades = async (pais) => {
    setLoading(true);
    try {
      const data = await ciudadesApi.getAll(pais);
      setCiudades(data);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCiudades([]);
    }
    setLoading(false);
  };

  const handleSelectPais = (pais) => {
    setSelectedPais(pais.codigo);
    setStep('ciudad');
  };

  const handleSelectCiudad = (ciudad) => {
    onSelect(ciudad);
  };

  const handleBack = () => {
    setStep('pais');
    setSelectedPais(null);
    setCiudades([]);
  };

  if (!isOpen) return null;

  const selectedPaisObj = paises.find(p => p.codigo === selectedPais);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        data-testid="city-selector-modal"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-full max-w-md bg-background rounded-3xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-primary/80 p-6 text-white text-center">
            <img src={LOGO_URL} alt="Mercapoli" className="w-16 h-16 mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-bold mb-2">
              ¡Bienvenido a Mercapoli!
            </h2>
            <p className="text-white/80 text-sm">
              {step === 'pais' 
                ? 'Selecciona tu país para comenzar'
                : `Selecciona tu ciudad en ${selectedPaisObj?.nombre}`
              }
            </p>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'pais' ? (
              // Paso 1: Selección de país
              <div className="space-y-3" data-testid="country-selector">
                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <Globe className="h-5 w-5" />
                  <span className="text-sm font-medium">¿Dónde te encuentras?</span>
                </div>
                
                {loadingPaises ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
                  </div>
                ) : (
                  <div className="max-h-[350px] overflow-y-auto space-y-2">
                    {paises.map((pais) => (
                      <motion.button
                        key={pais.codigo}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSelectPais(pais)}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-brand-primary hover:bg-brand-primary/5 transition-all"
                        data-testid={`country-${pais.codigo}`}
                      >
                        <span className="text-3xl">{BANDERAS[pais.codigo] || '🏳️'}</span>
                        <span className="font-medium text-lg flex-1 text-left">{pais.nombre}</span>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Paso 2: Selección de ciudad
              <div className="space-y-3" data-testid="city-selector">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
                >
                  <ChevronRight className="h-4 w-4 rotate-180" />
                  Cambiar país ({BANDERAS[selectedPais] || '🏳️'} {selectedPaisObj?.nombre})
                </button>

                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <MapPin className="h-5 w-5" />
                  <span className="text-sm font-medium">¿En qué ciudad estás?</span>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
                  </div>
                ) : ciudades.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No hay ciudades disponibles en este país aún.</p>
                    <p className="text-sm mt-2">Pronto estaremos disponibles aquí.</p>
                    <Button 
                      onClick={handleBack} 
                      variant="outline" 
                      className="mt-4"
                    >
                      Seleccionar otro país
                    </Button>
                  </div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {ciudades.map((ciudad) => (
                      <motion.button
                        key={ciudad.ciudad_id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleSelectCiudad(ciudad)}
                        className="w-full flex items-center gap-4 p-4 rounded-xl bg-card border border-border hover:border-brand-primary hover:bg-brand-primary/5 transition-all"
                        data-testid={`city-${ciudad.ciudad_id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                          <MapPin className="h-5 w-5 text-brand-primary" />
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-medium block">{ciudad.nombre}</span>
                          <span className="text-sm text-muted-foreground">
                            {ciudad.simbolo_moneda} {ciudad.moneda}
                          </span>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </motion.button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 pb-6">
            <p className="text-xs text-center text-muted-foreground">
              Puedes cambiar tu ubicación en cualquier momento desde el menú
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CitySelectorModal;
